import { dateId, excelDownloadId, searchId, segmentDisplayId } from "@/assets/googleTrackIds";
import { FiltersButton } from "@/components/Dataheader/FiltersButton";
import { FiltersDrawer } from "@/components/Dataheader/FiltersDrawer";
import { SettlementFullDetails, settlementPublicApis } from "@payping/api";
import { PhRDownloadSimple, PhRList, PhRSquaresFour } from "@payping/icons";
import { FiltersType, GridifyConditionEnum, GridifyLogicalEnum, createQuery } from "@payping/utils/api";
import { dateFormats, toJalali, toUtc } from "@payping/utils/dateUtils";
import { submitQueryParams } from "@payping/utils/query-string";
import {
  Button,
  DataHeader,
  DatePicker,
  Flex,
  Form,
  Grid,
  Input,
  Segmented,
  Select,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import qs from "query-string";
import { FC, ReactElement, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppSelector } from "@/utils/redux";
import { generateExcel } from "@/utils/excelGenerator";
import { DestinationBankObj, SettlementType, StatusObj } from "./types";
import { ListNumberQueryParamsType } from "../Request/types";
import { RequestListStringQueryParamsType } from "../types";

interface Props {
  retry: () => void;
  loading: boolean;
}

export const ListFilters: FC<Props> = (props) => {
  const { retry, loading } = props;
  const { t } = useAppTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [openDrawer, setOpenDrawer] = useState(false);
  const parsedSearch = qs.parse(location.search);
  const { code, iban, submittedTimeFrom, submittedTimeTo, withdrawDate, requestDate }: SettlementType = qs.parse(
    location.search,
  );
  const { segment }: RequestListStringQueryParamsType = qs.parse(location.search, {
    arrayFormat: "bracket",
  });
  const {
    status,
    destinationBank,
    pageNumber = 1,
    pageSize = 10,
  }: ListNumberQueryParamsType & SettlementType = qs.parse(location.search, {
    parseNumbers: true,
  });
  const listCount = useAppSelector((state) => state.cashOuts.filteredSettlementList?.count);
  const backFormat = dateFormats.standard.utcDateTime;
  const { xl, lg, xxl } = Grid.useBreakpoint();

  useEffect(() => {
    form.setFieldsValue({
      code,
      iban,
      submittedTime: [
        toUtc({ date: submittedTimeFrom, dateFormat: backFormat, toFormat: backFormat }).object,
        toUtc({ date: submittedTimeTo, dateFormat: backFormat, toFormat: backFormat }).object,
      ],
      searchVariant: ["iban", "code"].find((variant) => variant in parsedSearch) || "searchByType",
      destinationBank,
      status,
    });
  }, [
    status,
    destinationBank,
    iban,
    submittedTimeFrom,
    submittedTimeTo,
    code,
    withdrawDate,
    requestDate,
    form,
    backFormat,
    parsedSearch,
  ]);

  const onReset = (): void => {
    navigate({ pathname: location.pathname });
    form.resetFields();
    setOpenDrawer(false);
  };

  const [, downloadList] = useMessagedAsyncFn(async () => {
    const filter: FiltersType = [
      {
        code: [GridifyConditionEnum.Equal, code],
        destinationBank: [GridifyConditionEnum.Equal, destinationBank],
        iban: [
          GridifyConditionEnum.Equal,
          // eslint-disable-next-line no-nested-ternary
          iban ? (!iban.toUpperCase().startsWith("IR") ? `IR${iban}` : iban) : undefined,
        ],
        status: [GridifyConditionEnum.Equal, status],
        submittedTime: [GridifyConditionEnum.GreaterThan, submittedTimeFrom],
      },
      GridifyLogicalEnum.And,
      { submittedTime: [GridifyConditionEnum.LessThan, submittedTimeTo] },
    ];

    const allData = await settlementPublicApis.searchSettlementsListWithQueryGet(
      0,
      listCount,
      undefined,
      createQuery(filter),
    );

    const statusTag = (statusMap = status) => {
      if (statusMap) {
        const statusObj = StatusObj.filter((stat) => stat.no === parseInt(statusMap, 10));
        if (statusObj[0]) return statusObj[0].locale;
      }
      return t("unclear");
    };

    const excelData = allData?.data?.map((data: SettlementFullDetails) => [
      toJalali({ date: data.submittedTime, toFormat: dateFormats.ui.jalaliDateTime }).string,
      data.code,
      data.fullName,
      data.iban,
      data.clientRefId,
      data.persianBankName,
      data.bankTransferReference,
      data.amount,
      data.wage,
      t(`settlement.status.${statusTag(data.status?.toString() || "")}`),
      data.description,
    ]);
    if (excelData)
      generateExcel([
        [
          t("settlement.requestDate"),
          t("settlement.trackingCode"),
          t("fullName"),
          t("shaba"),
          t("settlement.clientRefId"),
          t("settlement.destinationBank"),
          t("settlement.bankTransferReference"),
          t("settlement.amount"),
          t("settlement.wage"),
          t("status"),
          t("description"),
        ],
        [...excelData],
      ]);
  }, [code, submittedTimeFrom, submittedTimeTo, status, destinationBank, iban, pageNumber, pageSize, listCount]);

  const mobileFiltersBadgeCount = !lg ? { code, iban, submittedTimeFrom } : undefined;

  return (
    <Form
      initialValues={{ searchVariant: "searchByType" }}
      onFinish={(values): void => {
        const newValues = {
          ...parsedSearch,
          code: values.code,
          iban: values.iban,
          submittedTimeFrom: values.submittedTime?.[0]?.format(backFormat),
          submittedTimeTo: values.submittedTime?.[1]?.format(backFormat),
          destinationBank: values.destinationBank,
          status: values.status,
        };
        submitQueryParams({
          navigate,
          newValues,
          parsedSearch,
          pathname: location.pathname,
          retry,
        });
        setOpenDrawer(false);
      }}
      form={form}
    >
      <DataHeader
        title={<Typography.Title level={4}>{t("settlement.report.title")}</Typography.Title>}
        filters={{
          segmentsButton: (
            <Segmented
              value={segment}
              onChange={(value) =>
                submitQueryParams({
                  navigate,
                  newValues: { segment: value },
                  parsedSearch: {},
                  pathname: location.pathname,
                  retry,
                })
              }
              options={[
                {
                  value: "settlement",
                  icon: <PhRList width={16} />,
                  label: t("settlement.report.title"),
                },
                {
                  value: "request",
                  icon: <PhRSquaresFour width={16} />,
                  label: t("settlement.requestsReport"),
                },
              ]}
              data-google-track={segmentDisplayId}
            />
          ),
          datepickerInput: xl && (
            <Form.Item key={1} name={"submittedTime"} noStyle>
              <DatePicker.RangePicker className="w-100" onChange={() => form.submit()} data-google-track={dateId} />
            </Form.Item>
          ),
          searchbarInput: xxl && (
            <Form.Item
              key={2}
              noStyle
              shouldUpdate={(prev, curr): boolean => prev.searchVariant !== curr.searchVariant}
            >
              {(form1): ReactElement => (
                <Form.Item name={form1.getFieldValue("searchVariant")} noStyle>
                  <Input.Search
                    disabled={form1.getFieldValue("searchVariant") === "searchByType"}
                    allowClear
                    addonBefore={
                      <Form.Item name={"searchVariant"} noStyle>
                        <Select dropdownMatchSelectWidth={false}>
                          <Select.Option disabled={true} value={"searchByType"}>
                            {t("settlement.searchBy")}
                          </Select.Option>
                          <Select.Option value={"iban"}>{t("shaba")}</Select.Option>
                          <Select.Option value={"code"}>{t("settlement.trackingCode")}</Select.Option>
                        </Select>
                      </Form.Item>
                    }
                    onSearch={() => form.submit()}
                    placeholder={t("common.search")}
                    data-google-track={searchId}
                  />
                </Form.Item>
              )}
            </Form.Item>
          ),
          filtersButton: (
            <>
              <FiltersButton
                onClick={() => setOpenDrawer(true)}
                params={{ destinationBank, status, ...mobileFiltersBadgeCount }}
              />

              <FiltersDrawer
                title={t("filter")}
                onClose={() => setOpenDrawer(false)}
                open={openDrawer}
                onClickApplyFilters={() => form.submit()}
                onClickResetFilters={() => onReset()}
                loading={loading}
              >
                <Flex vertical gap={8}>
                  {!xl ? (
                    <>
                      {/* < DatePicker > */}
                      <Form.Item key={1} label={t("date")} name={"submittedTime"} layout={"vertical"}>
                        <DatePicker.RangePicker className="w-100" data-google-track={dateId} />
                      </Form.Item>
                    </>
                  ) : null}

                  {/* < SearchVariant > */}
                  <Form.Item
                    label={t("search.search")}
                    layout={"vertical"}
                    shouldUpdate={(prev, curr): boolean => prev.searchVariant !== curr.searchVariant}
                  >
                    {(form1): ReactElement => (
                      <Form.Item name={form1.getFieldValue("searchVariant")} noStyle>
                        <Input.Search
                          disabled={form1.getFieldValue("searchVariant") === "searchByType"}
                          allowClear
                          addonBefore={
                            <Form.Item name={"searchVariant"} noStyle>
                              <Select dropdownMatchSelectWidth={false}>
                                <Select.Option disabled={true} value={"searchByType"}>
                                  {t("settlement.searchBy")}
                                </Select.Option>
                                <Select.Option value={"iban"}>{t("shaba")}</Select.Option>
                                <Select.Option value={"code"}>{t("settlement.trackingCode")}</Select.Option>
                              </Select>
                            </Form.Item>
                          }
                          placeholder={t("common.search")}
                          data-google-track={searchId}
                        />
                      </Form.Item>
                    )}
                  </Form.Item>
                  {/* </ SearchVariant > */}

                  {/* < Status > */}
                  <Form.Item label={t("status")} name={"status"} layout={"vertical"}>
                    <Select
                      allowClear
                      placeholder={t("status")}
                      className={"w-100"}
                      options={StatusObj.map((stat) => ({
                        label: t(`settlement.status.${stat.locale}`),
                        value: stat.no,
                      }))}
                    />
                  </Form.Item>

                  {/* < DestinationBank > */}
                  <Form.Item label={t("settlement.destinationBank")} name="destinationBank" layout={"vertical"}>
                    <Select
                      allowClear
                      placeholder={t("settlement.destinationBank")}
                      className={"w-100"}
                      options={DestinationBankObj.map((bank) => ({
                        label: t(`settlement.bankName.${bank.locale}`),
                        value: bank.no,
                      }))}
                    />
                  </Form.Item>
                </Flex>
              </FiltersDrawer>
            </>
          ),
          downloadButton: (
            <Form.Item key={4} noStyle>
              <Button
                className="w-100"
                icon={<PhRDownloadSimple width={14} height={14} />}
                onClick={() => downloadList()}
                key={1}
                data-google-track={excelDownloadId}
              >
                {t("downloadExcel")}
              </Button>
            </Form.Item>
          ),
        }}
      />
    </Form>
  );
};

export default ListFilters;
